/**
 * The file picker. One component, used by every upload surface.
 *
 * Presentational on purpose: it validates against `lib/upload` and hands the
 * caller a `File`, but it does not know about IndexedDB, agents or the bench.
 * That is what lets the Lockerroom dialog and an agent's step 1 share it
 * without either importing the other.
 *
 * Rejection is shown here, next to the control that caused it, rather than
 * being thrown or toasted — the user needs it beside the drop target while they
 * pick a different file.
 */

import { useId, useRef, useState } from 'react';
import type { DragEvent, JSX } from 'react';
import {
  ACCEPT_ATTRIBUTE,
  ACCEPTED_EXTENSIONS,
  MAX_FILE_BYTES,
  formatFileSize,
  validateFile,
  type UploadRejection,
} from '@/lib/upload';
import { Button } from './Button';
import { Icon } from './Icon';
import { Notice } from './Surfaces';

export interface FileDropProps {
  /** The chosen file, or null. Controlled by the caller. */
  readonly file: File | null;
  readonly onPick: (file: File | null) => void;
  /** Shown under the drop target. Defaults to the accepted types and cap. */
  readonly hint?: string;
  readonly disabled?: boolean;
}

export function FileDrop({ file, onPick, hint, disabled = false }: FileDropProps): JSX.Element {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejection, setRejection] = useState<UploadRejection | null>(null);
  const [dragging, setDragging] = useState(false);

  const accept = (candidate: File | undefined): void => {
    if (!candidate) return;
    const problem = validateFile(candidate);
    setRejection(problem);
    // A rejected file must not silently replace a good one already chosen.
    if (problem === null) onPick(candidate);
  };

  const clear = (): void => {
    setRejection(null);
    onPick(null);
    // Without this, re-picking the same file fires no change event.
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    accept(event.dataTransfer.files[0]);
  };

  if (file !== null) {
    return (
      <div className="stack-8">
        <div className="card between" style={{ padding: 12, boxShadow: 'none' }}>
          <div className="row" style={{ gap: 10, minWidth: 0 }}>
            <span style={{ color: 'var(--pnet-blue)' }}>
              <Icon name="file" />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="body-sm" style={{ fontWeight: 600, wordBreak: 'break-all' }}>
                {file.name}
              </div>
              <span className="caption">{formatFileSize(file.size)}</span>
            </div>
          </div>
          <Button variant="secondary" size="sm" icon="x" onClick={clear} disabled={disabled}>
            Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack-8">
      <div
        className="ph"
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={onDrop}
        style={{
          textAlign: 'center',
          padding: 22,
          transition: 'background 120ms, border-color 120ms',
          ...(dragging ? { borderColor: 'var(--pnet-blue)', background: '#F7F9FD' } : {}),
          ...(disabled ? { opacity: 0.55 } : {}),
        }}
      >
        <label
          className="btn btn--secondary"
          htmlFor={inputId}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <Icon name="upload" />
          Choose file
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          disabled={disabled}
          style={{ display: 'none' }}
          onChange={(event) => {
            accept(event.target.files?.[0]);
          }}
        />
        <p className="caption" style={{ marginTop: 8 }}>
          {hint ??
            `Drag a file here, or choose one. ${ACCEPTED_EXTENSIONS.join(', ')} · up to ${formatFileSize(MAX_FILE_BYTES)}`}
        </p>
      </div>

      {rejection !== null && (
        <Notice tone="bad" icon="alert">
          {rejection.message}
        </Notice>
      )}
    </div>
  );
}
